import { baseSchema, Update } from './base'
import { generate } from './generate'
import { z } from 'zod'
import { parseSpaceName } from '../helpers'
import { invariant } from '@epic-web/invariant'
import { SpaceLineage } from '../../db'
import { Branch, branchLineageSchema } from './branch'

export const commitSchema = baseSchema.extend({
  branchId: z.string(),
  branchLineage: z.lazy(() => branchLineageSchema).optional(),
  message: z.string(),
  createdAt: z.string(),
  authorId: z.string(),
  parentCommitId: z.string().nullable(),
  contributors: z.array(z.string()).nullish(),
  mergeParentCommitId: z.string().nullish(), // used for merge commits
  rootBlockId: z.string(),
  hash: z.string(),
  repoId: z.string(),
})

export type Commit = z.infer<typeof commitSchema>
export type CommitQueryResult = { result: Commit; isFallback: boolean } | null
export type BranchOrCommitQueryResult = {
  result: Commit | Branch
  isFallback: boolean
} | null
export type CommitUpdate = Update<Commit>
export type MergeCommit = Omit<
  Commit,
  'secondParentCommitId' | 'parentCommitId'
> & {
  parentCommitId: string
  mergeParentCommitId: string
}

export const commitBaseOps = generate('commit', commitSchema.parse)

export function isMergeCommit(commit: Commit): commit is MergeCommit {
  return Boolean(commit.mergeParentCommitId)
}

type CanReadCommitParams = {
  commit: Commit
  spaceId: string
  spaceLineage: SpaceLineage
}

export function canReadCommit({ commit, spaceLineage }: CanReadCommitParams) {
  return spaceLineage.some((space) => {
    const parsedSpaceId = parseSpaceName(space)
    invariant(parsedSpaceId.spaceType === 'repo', 'spaceId must be a repo')
    return parsedSpaceId.repoId === commit.repoId
  })
}

export function canReadCommitOrThrow({
  commit,
  spaceId,
  spaceLineage,
}: CanReadCommitParams) {
  if (!canReadCommit({ commit, spaceId, spaceLineage })) {
    throw new Error(`Unauthorized - Cannot read commit ${commit.id}`)
  }
}
